import type { CreatePositionFormValues } from './types';

/**
 * These endpoints are Workday's SOAP API behind Kombo's passthrough — no other
 * connector (BambooHR, HiBob, TriNet, …) exposes the operations, so the entry
 * point is pinned to this one integration type rather than to "any Kombo HRIS".
 */
export const WORKDAY_INTEGRATION_TYPE = 'kombo/workday';

/** LaunchDarkly `t_workday_position_creation`, camelCased by the LD SDK. */
export const WORKDAY_POSITION_FLAG = 'tWorkdayPositionCreation';

export type PositionFieldName = keyof CreatePositionFormValues;

export interface PositionFieldConfig {
  /** Payload key, and the Formik field name. */
  name: PositionFieldName;
  label: string;
  /**
   * `type` query value for `position_reference_options`. Absent for the free
   * text field.
   */
  referenceType?: string;
  /**
   * Only Team and Job title are required. The other six are optional on
   * `create_position` — Workday accepts a position without them, so blocking
   * on them would reject submissions the backend would have taken.
   */
  required?: boolean;
  /**
   * Hidden when `position_reference_options` returns an empty list: some
   * Workday tenants do not use this reference at all, and an empty dropdown is
   * worse than no field.
   */
  hideWhenEmpty?: boolean;
}

/**
 * The create-position form, in render order: the two required fields first,
 * then the six Workday accepts without.
 *
 * Reference `type` values are Workday reference-ID type names, passed straight
 * through to `position_reference_options?type=...`.
 */
export const POSITION_FIELDS: PositionFieldConfig[] = [
  {
    name: 'supervisory_organization_id',
    label: 'Team',
    referenceType: 'Organization_Reference_ID',
    required: true,
  },
  {
    name: 'job_posting_title',
    label: 'Job title',
    // Free text, prefilled from the Kula job and editable.
    required: true,
  },
  {
    name: 'job_profile_id',
    label: 'Job profile',
    referenceType: 'Job_Profile_ID',
  },
  {
    name: 'location_id',
    label: 'Location',
    referenceType: 'Location_ID',
  },
  {
    name: 'time_type_id',
    label: 'Time type',
    referenceType: 'Position_Time_Type_ID',
  },
  {
    name: 'worker_type_id',
    label: 'Worker type',
    referenceType: 'Worker_Type_ID',
  },
  {
    name: 'company_id',
    label: 'Company',
    referenceType: 'Company_Reference_ID',
    hideWhenEmpty: true,
  },
  {
    name: 'cost_center_id',
    label: 'Cost centre',
    referenceType: 'Cost_Center_Reference_ID',
    hideWhenEmpty: true,
  },
];

/**
 * Copy for the Workday entry in the generic "Add to HRIS" surfaces (the action
 * submenu and the Add-to-HRIS modal), where the position flow replaces the
 * usual "Add to Workday" entry until a position is ready.
 */
export const WORKDAY_POSITION_ACTION_LABELS = {
  create: 'Create Workday position',
  syncing: 'Syncing with Workday…',
} as const;

export const EMPTY_POSITION_FORM_VALUES: CreatePositionFormValues = {
  supervisory_organization_id: '',
  company_id: '',
  cost_center_id: '',
  job_profile_id: '',
  location_id: '',
  time_type_id: '',
  worker_type_id: '',
  job_posting_title: '',
};
